import { Box, Container, Divider, Tab, Tabs, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Grid from '@mui/material/Grid2';
import React, { useEffect, useState } from 'react';
import { theme } from '../../../theme';
import { cardGridSize } from '../../../utils/constants';
import { AppBarPadding, BottomPadding } from '../../../utils/helperUtils';
import ApproveVendorCardComponent from '../components/ApproveVendorCardComponent';
import ManageEventsCardComponent from '../components/ManageEventsCardComponent';
import SearchComponent from '../components/SearchComponent';
import { useParams } from 'react-router-dom';
import { ApproveEventListingAuth, fetchEventApplicationsOrganizerAuth, fetchPublicEventDetail, WithdrawEventApplicationAuth } from '../../../services/eventServices';
import { useAuth } from '../../../context/AuthContext';
import LoadingComponent from '../../components/LoadingComponent';
import { useSnackbar } from '../../../context/SnackbarContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function ApproveVendorsView() {

    const { slug } = useParams();
    const { login, logout } = useAuth();

    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [eventApplications, setEventApplications] = useState([]);
    const [eventInfo, setEventInfo] = useState(null);
    const [error, setError] = useState("");
    const [tabValue, setTabValue] = React.useState('1');
    const [refreshKey, setRefreshKey] = useState(0);

    const showSnackbar = useSnackbar();

    const applicationType = {
        "pending": "1",
        "accepted": "2",
        "paid": "2",
        "rejected": "3"
    }

    const handleSubmit = async (status, vendorSlug, comment="") => {
        try {
            setLoading(true)
            const response = await ApproveEventListingAuth(slug, vendorSlug, status, comment, login, logout);
            console.log(`Application ${status}:`, response);
            showSnackbar("Event Application updated!", "success");
            setRefreshKey(prev => prev + 1);
        } catch (err) {
            setError(err.message)
            console.error(`Error submitting application status (${status}):`, err);
        }
        finally{
            setLoading(false)
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    useEffect(() => {
        const fetchEventApplications = async () => {
            setLoading(true);
            try {
                const response = await fetchEventApplicationsOrganizerAuth(slug, login, logout);
                if (response.success) {
                    setEventApplications(response.data);
                } else {
                    console.error("Failed to fetch organized events:", response.error);
                }
            } catch (err) {
                console.error("Unexpected error fetching organized events:", err);
                setError(err.message)
            }
            finally {
                setLoading(false);
            }
        };

        const fetchEvent = async () => {
            setLoading(true);
            try {
                const response = await fetchPublicEventDetail(slug, login, logout);

                if (response.success) {
                    setEventInfo(response.data);
                } else {
                    console.error("API Error:", response.error);
                    setError(response.error || "Failed to fetch event details.");
                }
            } catch (err) {
                console.error("Unexpected Error:", err);
                setError(err.message || "Something went wrong while fetching the event.");
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchEventApplications();
            fetchEvent();
        }
    }, [slug, refreshKey]);

    if (loading) {
        return (
            <LoadingComponent />
        );
    }

    return (
        <Box bgcolor={theme.colors.events.primary}>
            <Container maxWidth="xl" className="events-box-container">
                <AppBarPadding />
                <Grid container justifyContent={'center'}  >
                    <Grid width={'100%'}>
                        <Grid size={cardGridSize}>
                            <ManageEventsCardComponent eventInfo={eventInfo} />
                        </Grid>
                        <Typography fontSize={30} color={theme.colors.events.secondary}>
                            <Box sx={{ fontWeight: 'bold', mb: 1, mt: 1 }}>
                                Approve Vendors ✅
                            </Box>
                        </Typography>
                        <Divider sx={{ borderBottomWidth: 2, bgcolor: theme.colors.events.secondary }} />
                        {/* <SearchComponent
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery} /> */}
                        {
                            error && (
                                <Box textAlign="center" mt={4} className="box-container">
                                    <Typography color="error" variant="h6">⚠️ {error || 'Something went wrong, please try again later'}</Typography>
                                </Box>
                            )
                        }
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            TabIndicatorProps={{ style: { backgroundColor: theme.colors.events.secondary } }}
                            sx={{
                                color: `${theme.colors.events.secondary} !important`,
                                '& .MuiTab-root': {
                                    color: `${theme.colors.events.secondary} !important`,
                                },
                            }}
                        >
                            <Tab
                                label={`Pending (${eventApplications.filter(app => applicationType[app.application_status] === '1').length})`}
                                value="1"
                            />
                            <Tab
                                label={`Accepted (${eventApplications.filter(app => applicationType[app.application_status] === '2').length})`}
                                value="2"
                            />
                            <Tab
                                label={`Rejected (${eventApplications.filter(app => applicationType[app.application_status] === '3').length})`}
                                value="3"
                            />
                        </Tabs>
                        {Object.entries(
                          eventApplications
                            .filter(app => applicationType[app.application_status] === tabValue)
                            .reduce((groups, app) => {
                              const key = app.booth_type_name || 'Other';
                              if (!groups[key]) groups[key] = [];
                              groups[key].push(app);
                              return groups;
                            }, {})
                        ).map(([boothType, apps], idx) => (
                            <Accordion
                                key={idx}
                                defaultExpanded
                                square
                                disableGutters
                                elevation={0}
                                sx={{
                                    bgcolor: 'transparent',
                                    borderBottom: `1px solid ${theme.colors.events.secondary}`,
                                    '&:before': { display: 'none' } // removes default grey divider
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                      <ExpandMoreIcon
                                        sx={{
                                          color: theme.colors.events.secondary,
                                          border: `2px solid ${theme.colors.events.secondary}`,
                                          borderRadius: '50%',
                                          padding: '2px'
                                        }}
                                      />
                                    }
                                >
                                    <Typography fontWeight={600} color={theme.colors.events.secondary}>
                                      Booth: {boothType}
                                      {eventInfo?.booth_types && (() => {
                                        const booth = eventInfo.booth_types.find(b => b.name === boothType);
                                            return booth ? ` – ${booth.booths_sold}/${booth.quantity_available} sold` : '';
                                      })()}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 0 }}>
                                    <Grid container spacing={2}>
                                        {apps.map((eventApplication, index) => (
                                            <Grid key={index} size={{ xs: 6, md: 6 }}>
                                                <ApproveVendorCardComponent
                                                    eventApplicationInfo={eventApplication}
                                                    handleSubmit={handleSubmit}
                                                    eventInfo={eventInfo}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Grid>
                </Grid>
                <BottomPadding />
            </Container>
        </Box>
    )
}

export default ApproveVendorsView